import { every } from 'lodash';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ICustomControlData } from './customControlData';
import { Feedback, InputCheckbox } from '../../../../elements/common';
import { FlexColumns } from '../../../../elements/FlexColumns';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputTextCheckbox } from '../../../../elements/InputTextCheckbox';

interface IIndicatieAddEditProps {
    customControlData: ICustomControlData;
    errors: ValidationErrors;
    handleChangeCustomControl: (customControl: Partial<ICustomControlData>) => Promise<void>;
    isRequired?: boolean;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export function IndicatieAddEdit(props: IIndicatieAddEditProps) {
    return (
        <>
            <HorizontalFormGroup
                hasErrors={
                    (props.errors.has('customControlData.indicatie') && hasErrors(props.customControlData)) ||
                    props.errors.has('customControlData.indicatie.dyspnoeDeffort') ||
                    props.errors.has('customControlData.indicatie.controleTherapie') ||
                    props.errors.has('customControlData.indicatie.hartfalen') ||
                    props.errors.has('customControlData.indicatie.ritmeGeleidingsstoornissen') ||
                    props.errors.has('customControlData.indicatie.decompensatioCordis') ||
                    props.errors.has('customControlData.indicatie.duizeling') ||
                    props.errors.has('customControlData.indicatie.ventrikelHypertrofie') ||
                    props.errors.has('customControlData.indicatie.hartkloppingen') ||
                    props.errors.has('customControlData.indicatie.kleplijden') ||
                    props.errors.has('customControlData.indicatie.verdenkingIschemie') ||
                    props.errors.has('customControlData.indicatie.hypertensie') ||
                    props.errors.has('customControlData.indicatie.typischeAngina') ||
                    props.errors.has('customControlData.indicatie.algemeenOnderzoek') ||
                    props.errors.has('customControlData.indicatie.atypischeAngina') ||
                    props.errors.has('customControlData.indicatie.geruststelling') ||
                    props.errors.has('customControlData.indicatie.andersText')
                }
            >
                <FormLabel isRequired={props.isRequired}>Indicatie</FormLabel>
                <InputContainer>
                    <FlexColumns sm={12}>
                        <InputCheckbox
                            checked={props.customControlData.indicatie.dyspnoeDeffort}
                            error={props.errors.get('customControlData.indicatie.dyspnoeDeffort')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, dyspnoeDeffort: value },
                                })
                            }
                            text="Dyspnoe d'effort"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.controleTherapie}
                            error={props.errors.get('customControlData.indicatie.controleTherapie')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, controleTherapie: value },
                                })
                            }
                            text="Controle therapie"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.hartfalen}
                            error={props.errors.get('customControlData.indicatie.hartfalen')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, hartfalen: value },
                                })
                            }
                            text="Hartfalen"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.ritmeGeleidingsstoornissen}
                            error={props.errors.get('customControlData.indicatie.ritmeGeleidingsstoornissen')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: {
                                        ...props.customControlData.indicatie,
                                        ritmeGeleidingsstoornissen: value,
                                    },
                                })
                            }
                            text="Ritme / geleidingsstoornissen"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.decompensatioCordis}
                            error={props.errors.get('customControlData.indicatie.decompensatioCordis')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, decompensatioCordis: value },
                                })
                            }
                            text="Decompensatio cordis"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.duizeling}
                            error={props.errors.get('customControlData.indicatie.duizeling')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, duizeling: value },
                                })
                            }
                            text="Duizeling / collapsneigingen / wegraking"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.ventrikelHypertrofie}
                            error={props.errors.get('customControlData.indicatie.ventrikelHypertrofie')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, ventrikelHypertrofie: value },
                                })
                            }
                            text="Ventrikel hypertrofie"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.hartkloppingen}
                            error={props.errors.get('customControlData.indicatie.hartkloppingen')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, hartkloppingen: value },
                                })
                            }
                            text="Hartkloppingen / overslag"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.kleplijden}
                            error={props.errors.get('customControlData.indicatie.kleplijden')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, kleplijden: value },
                                })
                            }
                            text="Kleplijden"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.verdenkingIschemie}
                            error={props.errors.get('customControlData.indicatie.verdenkingIschemie')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, verdenkingIschemie: value },
                                })
                            }
                            text="Verdenking ischemie / infarct"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.hypertensie}
                            error={props.errors.get('customControlData.indicatie.indHypertensie')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, hypertensie: value },
                                })
                            }
                            text="Hypertensie"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.typischeAngina}
                            error={props.errors.get('customControlData.indicatie.typischeAngina')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, typischeAngina: value },
                                })
                            }
                            text="Typische angina pectoris"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.algemeenOnderzoek}
                            error={props.errors.get('customControlData.indicatie.algemeenOnderzoek')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, algemeenOnderzoek: value },
                                })
                            }
                            text="Algemeen onderzoek"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.atypischeAngina}
                            error={props.errors.get('customControlData.indicatie.atypischeAngina')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, atypischeAngina: value },
                                })
                            }
                            text="Atypische angina pectoris"
                        />
                        <InputCheckbox
                            checked={props.customControlData.indicatie.geruststelling}
                            error={props.errors.get('customControlData.indicatie.geruststelling')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, geruststelling: value },
                                })
                            }
                            text="Geruststelling"
                        />
                        <InputTextCheckbox
                            error={props.errors.get('customControlData.indicatie.andersText')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({
                                    indicatie: { ...props.customControlData.indicatie, andersText: value },
                                })
                            }
                            onDoValidate={async () => await props.validateOneCustomControl('indicatie.andersText')}
                            placeholder="Anders, namelijk..."
                            text={props.customControlData.indicatie.andersText}
                        />
                    </FlexColumns>
                    {props.errors.has('customControlData.indicatie') && hasErrors(props.customControlData) && (
                        <Feedback error={props.errors.get('customControlData.indicatie')} />
                    )}
                </InputContainer>
            </HorizontalFormGroup>
        </>
    );
}

function hasErrors(customControlData: ICustomControlData) {
    return every(
        Object.values(customControlData.indicatie),
        (value: string | boolean | null) => value === false || value === null
    );
}

export function checkForRequiredIndicaties(customControlData: ICustomControlData, errors: ValidationErrors) {
    if (hasErrors(customControlData)) {
        errors = errors.set('customControlData.indicatie', 'any_required');
    }

    return errors;
}
